import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { customerAPI } from '../../services/api';
import CustomerAppInstall from '../../components/customer/CustomerAppInstall';
import toast from 'react-hot-toast';
import { CreditCard, Wallet, MapPin, Tv, Wifi, ChevronDown } from 'lucide-react';
import AdBanner from '../../components/common/AdBanner';
import BroadbandHero from '../../components/customer/BroadbandHero';
import CatvHero from '../../components/customer/CatvHero';
import ServiceInterestCard from '../../components/customer/ServiceInterestCard';
import { customerTypeLabel } from '../../utils/customerTypeLabel';
import Over90DaysScreen from '../../components/customer/Over90DaysScreen';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

// The "Interested in Broadband? Request Today" upsell card (and its tab) for CATV-only
// customers who don't have broadband yet. CatvHero's own tab/suggestion logic is untouched.
const SHOW_BROADBAND_SUGGESTION = true;

export default function CustomerDashboard() {
  const [customer, setCustomer] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [switching, setSwitching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const catvRef = useRef(null);
  const bbRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [{ data: meRes }, accRes] = await Promise.all([
          customerAPI.getMe(),
          customerAPI.getMyAccounts().catch(() => null),
        ]);
        setCustomer(meRes.data);
        setAccounts(accRes?.data?.data || []);
      } catch {
        toast.error('Failed to load account data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Only relevant when this login fronts more than one CableTV (or Broadband)
  // connection — e.g. two separate flats. Re-fetches /me scoped to the chosen
  // account instead of whichever one the login defaults to.
  const switchAccount = async (id) => {
    if (!customer || id === customer._id) return;
    setSwitching(true);
    try {
      const { data } = await customerAPI.getMe(id);
      setCustomer(data.data);
      setActiveTab(null);
    } catch {
      toast.error('Failed to switch account');
    } finally {
      setSwitching(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
  if (!customer) return null;

  const linked = customer.linkedAccount || null;
  // A CATV connection and a Broadband connection are sometimes registered as two
  // separate accounts sharing one login (linked via the admin's "Link Account" panel) —
  // combine them here so the portal shows both instead of just this login's own record.
  const bbSource   = customer.customerType !== 'catv' ? customer : linked;
  const catvSource = customer.customerType !== 'broadband' ? customer : linked;
  const hasBB   = (bbSource?.customerType === 'broadband' || bbSource?.customerType === 'both') && !!bbSource?.broadband;
  const hasCatv = (catvSource?.customerType === 'catv' || catvSource?.customerType === 'both') && !!catvSource?.catv;
  // Same "Service Type" label shown on the Profile page — CableTV / Broadband / combo,
  // not the CATV package's own genre (basic/premium/sports/etc).
  const serviceType = hasBB && hasCatv ? 'CableTV + Broadband' : customerTypeLabel(customer.customerType);
  const balance = (customer.outstandingBalance || 0) + (linked?.outstandingBalance || 0);
  const firstName = (customer.name || '').split(' ')[0];
  // The broadband tab/section only appears when the customer actually has it, or the
  // upsell suggestion is turned back on — otherwise a catv-only customer would get a tab
  // that scrolls to nothing.
  const showBroadbandSection = hasBB || SHOW_BROADBAND_SUGGESTION;
  const tab = activeTab || (hasCatv ? 'catv' : 'broadband');
  const order = hasCatv
    ? ['catv', ...(showBroadbandSection ? ['broadband'] : [])]
    : (showBroadbandSection ? ['broadband', 'catv'] : ['catv']);

  const goToTab = (key) => {
    setActiveTab(key);
    const ref = key === 'catv' ? catvRef : bbRef;
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div>
      {/* Welcome */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 700 }}>Hello, {firstName || 'there'} 👋</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '.87rem', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
            <span>Customer ID: {customer.customerId}</span>
            {customer.address?.area && (<><span>·</span><MapPin size={12} /><span>{customer.address.area}</span></>)}
          </p>
        </div>
        <span className={`badge badge-${customer.status === 'active' || !customer.status ? 'success' : customer.status === 'suspended' ? 'warning' : 'danger'}`} style={{ flexShrink: 0, textTransform: 'capitalize' }}>
          {customer.status || 'active'}
        </span>
      </div>

      <div style={{ marginBottom: 20 }}><CustomerAppInstall /></div>

      {/* Account switcher — only shown when this login fronts more than one
          connection (e.g. two CableTV connections in two different flats).
          A single account, or a linked CATV+Broadband pair, still shows just
          the tabs below, unchanged. */}
      {accounts.length > 1 && (
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <select
            value={customer._id}
            disabled={switching}
            onChange={(e) => switchAccount(e.target.value)}
            style={{
              width: '100%', appearance: 'none', WebkitAppearance: 'none',
              padding: '12px 38px 12px 14px', borderRadius: 12, border: '1px solid var(--border)',
              background: 'var(--bg-card)', color: 'var(--text)', fontSize: '.85rem', fontWeight: 600,
              cursor: switching ? 'wait' : 'pointer', opacity: switching ? .6 : 1,
            }}
          >
            {accounts.map(acc => (
              <option key={acc.id} value={acc.id}>
                {acc.label}{acc.outstandingBalance > 0 ? ` — Due ₹${fmt(acc.outstandingBalance)}` : ''}
              </option>
            ))}
          </select>
          <ChevronDown size={16} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
        </div>
      )}

      {/* Expired more than 90 days: only the message for this account (the account switcher above
          stays, so a login with another account can still switch to it). */}
      {customer.expiredOver90Days && <Over90DaysScreen info={customer.expiredOver90Days} />}

      {!customer.expiredOver90Days && (<>
      {/* CableTV / Broadband tabs — sit right below the name/ID/address header.
          Both sections always render below in sequence; tapping a tab just scrolls
          to that section. A service the customer doesn't have renders as an
          "Interested? Request today" card instead of the live hero. */}
      {order.length > 1 && (
        <div style={{
          display: 'flex', gap: 5, marginBottom: 16, background: 'var(--bg)',
          padding: 5, borderRadius: 14, border: '1px solid var(--border)',
        }}>
          {[
            { key: 'catv', label: 'CableTV', Icon: Tv },
            ...(showBroadbandSection ? [{ key: 'broadband', label: 'Broadband', Icon: Wifi }] : []),
          ].map(({ key, label, Icon }) => {
            const active = tab === key;
            return (
              <button key={key} onClick={() => goToTab(key)} style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                padding: '10px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                fontWeight: 700, fontSize: '.85rem',
                background: active ? 'var(--primary)' : 'transparent',
                color: active ? '#fff' : 'var(--text-muted)',
              }}>
                <Icon size={15} /> {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Promo banner — above the outstanding balance so it's the first thing seen. */}
      <AdBanner />

      {/* Outstanding balance banner — only shown when something is actually due */}
      {balance > 0 && (
        <div className="card" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
          padding: '16px 20px', marginBottom: 20, borderLeft: '3px solid #dc2626',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#fee2e2',
            }}>
              <Wallet size={20} color="#dc2626" />
            </div>
            <div>
              <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
                Outstanding Balance{linked ? ' (combined)' : ''}
              </div>
              <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#dc2626' }}>₹{fmt(balance)}</div>
            </div>
          </div>
          <Link to="/portal/payments" className="btn btn-primary" style={{ flexShrink: 0 }}>
            <CreditCard size={14} style={{ marginRight: 6 }} /> Pay Now
          </Link>
        </div>
      )}

      {order.map((service) => (
        <React.Fragment key={service}>
          {service === 'catv'
            ? <div ref={catvRef}>{hasCatv ? <CatvHero data={catvSource.catv} connectionId={catvSource.customerId} durationType={catvSource.billing?.durationType} status={catvSource.status} serviceType={serviceType} /> : <ServiceInterestCard service="catv" />}</div>
            : <div ref={bbRef}>{hasBB ? <BroadbandHero data={bbSource.broadband} kind="broadband" connectionId={bbSource.customerId} durationType={bbSource.billing?.durationType} status={bbSource.status} /> : <ServiceInterestCard service="broadband" />}</div>}
        </React.Fragment>
      ))}

      {/* Contact ISP */}
      <div style={{ padding: 16, background: 'var(--primary-light)', borderRadius: 10, fontSize: '.85rem', color: 'var(--primary)', border: '1px solid rgba(30,58,138,.19)' }}>
        <strong>📞 Need help?</strong> Contact our support team for billing, technical issues, or plan upgrades. Visit your nearest office or raise a support ticket above.
      </div>
      </>)}
    </div>
  );
}
